import { createFileRoute } from "@tanstack/react-router";
import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { PageHeader } from "@dmca/frontend/components/nav";
import { SearchBar } from "@dmca/frontend/components/input";
import { Button } from "@dmca/frontend/components/button";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import {
  RFIDTagHistoryWidget,
  RFIDWidget,
  UpdateProductionJobModal,
} from "@dmca/frontend/widgets/rfid";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import { useTRPC } from "@dmca/frontend/trpc";
import { useQuery } from "@tanstack/react-query";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useRef, useState } from "react";
import type { IProductionJobWithLocation } from "@dmca/models/interface/production-job";
import { DateTime } from "@dmca/utils/luxon";

export const Route = createFileRoute("/_authed/job-check")({
  component: JobCheckPage,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("ProductionJob::SearchTag", context);
  },
  head: () => ({
    meta: [{ title: "Job Check | DNA Dashboard" }],
  }),
});

function JobCheckPage() {
  const trpc = useTRPC();

  const [inputValue, setInputValue] = useState("");
  const [submittedId, setSubmittedId] = useState("");
  const [scanning, setScanning] = useState(false);
  const scanBuffer = useRef("");

  const [selectedJob, setSelectedJob] = useState<
    IProductionJobWithLocation | undefined
  >();
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  // EPCs are stored with exactly one leading zero; strip all then re-add one
  const strippedId = submittedId.replace(/^0+/, "");
  const epc = strippedId ? `0${strippedId}` : "";

  const { data: jobData, isFetching: jobFetching } = useQuery({
    ...trpc.productionJob.getAllWithLocationPaginated.queryOptions({
      searchTerm: strippedId,
      page: 0,
      limit: 1,
    }),
    enabled: !!strippedId,
  });

  const job = jobData?.data?.[0];

  const {
    data: historyFirstPage,
    isFetching: historyFetching,
    error: historyError,
  } = useQuery({
    ...trpc.rfid.findTagLocationHistoryWithPagination.queryOptions({
      epc,
      cursor: 0,
      limit: 1,
    }),
    enabled: !!epc,
  });

  const currentLocation = historyFirstPage?.data?.history?.[0];
  const totalDuration = historyFirstPage?.data?.totalDuration;
  const currentLocationId = currentLocation?.locationId ?? job?.locationId;
  const currentLocationName =
    currentLocation?.location ?? job?.locationName ?? "—";

  const { data: cells = [] } = useQuery(trpc.cell.getAll.queryOptions());

  const currentCell = cells.find((c) => c.cellId === currentLocationId);

  const isLoading = jobFetching || historyFetching;

  const submitSearch = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSubmittedId(trimmed);
  }, []);

  const handleScanKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        const buffered = scanBuffer.current;
        scanBuffer.current = "";
        setInputValue(buffered);
        submitSearch(buffered);
        setScanning(false);
      } else if (e.key.length === 1) {
        scanBuffer.current += e.key;
      }
    },
    [submitSearch]
  );

  useEffect(() => {
    if (scanning) {
      scanBuffer.current = "";
      window.addEventListener("keydown", handleScanKeyDown);
    } else {
      window.removeEventListener("keydown", handleScanKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleScanKeyDown);
    };
  }, [scanning, handleScanKeyDown]);

  const toggleScan = () => {
    setInputValue("");
    setScanning((prev) => !prev);
  };

  return (
    <Container>
      <PageHeader>Job Check</PageHeader>

      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center" }}>
        <ButtonBase
          data-testid="scan-toggle-btn"
          onClick={toggleScan}
          sx={{
            bgcolor: scanning ? "success.main" : "action.disabledBackground",
            color: scanning ? "success.contrastText" : "text.primary",
            px: 2,
            py: 1.5,
            borderRadius: "50px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            fontSize: "0.875rem",
            userSelect: "none",
            transition: "all 0.2s",
          }}
        >
          <QrCodeScannerIcon fontSize="small" />
          {scanning ? "SCANNING…" : "SCAN"}
        </ButtonBase>
        <SearchBar
          data-testid="job-id-input"
          placeholder="Enter work order number"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submitSearch(inputValue);
          }}
          sx={{ flex: 1 }}
        />
        <Button
          data-testid="search-btn"
          onClick={() => submitSearch(inputValue)}
          disabled={!inputValue.trim()}
          loading={isLoading}
          startIcon={<SearchIcon />}
          sx={{ minHeight: 44 }}
        >
          Look Up
        </Button>
      </Stack>

      {submittedId && (
        <Paper sx={{ position: "relative", overflow: "hidden" }} elevation={3}>
          {isLoading && (
            <CircularProgress
              size={32}
              sx={{ position: "absolute", top: 16, right: 16 }}
            />
          )}

          <Box
            sx={{
              opacity: isLoading ? 0.4 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {!jobFetching && jobData !== undefined && !job && (
              <Typography color="error" align="center" sx={{ py: 2 }}>
                No job found for &ldquo;{strippedId}&rdquo;.
              </Typography>
            )}

            {historyError && (
              <Box sx={{ p: 2, pb: 0 }}>
                <Alert severity="error">
                  Error fetching location for tag {epc}.
                </Alert>
              </Box>
            )}

            {job && !historyError && (
              <Box
                data-testid="job-current-location-panel"
                sx={{
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  p: { xs: 2, md: 3 },
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Current Location
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {currentLocationName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.78 }}>
                      {currentLocationId
                        ? `Cell ID: ${currentLocationId}`
                        : "No cell recorded"}
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ minWidth: { md: 360 } }}
                  >
                    <HeaderStat
                      data-testid="job-check-info-tile-plant"
                      icon={<BusinessIcon fontSize="small" />}
                      label="Plant"
                      value={currentLocation?.plantName ?? "—"}
                      detail={
                        currentLocation?.plantId
                          ? `Plant ID: ${currentLocation.plantId}`
                          : "No plant recorded"
                      }
                    />
                    <HeaderStat
                      data-testid="job-check-info-tile-observed-duration"
                      icon={<AccessTimeIcon fontSize="small" />}
                      label="Observed Duration"
                      value={
                        totalDuration
                          ? `${totalDuration.as("hours").toFixed(1)} hours`
                          : "Unknown"
                      }
                      detail={
                        totalDuration
                          ? `${totalDuration.as("days").toFixed(2)} days total`
                          : "Across recorded history"
                      }
                    />
                  </Stack>
                </Stack>
              </Box>
            )}

            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <DetailPanel
                    data-testid="job-details-panel"
                    rows={[
                      ["JOB NUMBER", job?.id ?? strippedId],
                      ["JOB NAME", job?.name ?? "—"],
                    ]}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <DetailPanel
                    data-testid="job-dates-panel"
                    rows={[
                      [
                        "DUE DATE",
                        job?.dueDate?.toFormat("MM/dd/yyyy") ?? "—",
                        job?.dueDate && job.dueDate < DateTime.now()
                          ? "error.main"
                          : undefined,
                      ],
                      [
                        "CREATED AT",
                        job?.createdAt?.toFormat("MM/dd/yyyy") ?? "—",
                      ],
                    ]}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                  <DetailPanel
                    data-testid="job-status-panel"
                    rows={[
                      [
                        "STATUS",
                        job ? (job.completedDate ? "CLOSED" : "OPEN") : "—",
                      ],
                      [
                        "LAST SCANNED",
                        currentLocation?.from.toFormat("MM/dd/yyyy") ?? "—",
                      ],
                    ]}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                {/* Tag History Widget */}
                <Grid size={{ xs: 12, lg: 4 }}>
                  {epc && (
                    <RFIDTagHistoryWidget
                      epc={epc}
                      elevation={2}
                      data-testid="job-check-tag-history"
                    />
                  )}
                </Grid>

                {/* RFID Widget — jobs at the current cell */}
                <Grid size={{ xs: 12, lg: 8 }}>
                  {currentCell && (
                    <RFIDWidget
                      location={currentCell}
                      searchOptions={{
                        search: "",
                        showProject: "both",
                        showHighPriorityOnly: false,
                      }}
                      setSelectedJob={(job) => {
                        setSelectedJob(
                          typeof job === "function" ? job(selectedJob) : job
                        );
                      }}
                      setShowSelectionModal={(val) => {
                        const open =
                          typeof val === "function"
                            ? val(showUpdateModal)
                            : val;
                        setShowUpdateModal(open);
                      }}
                      elevation={2}
                      data-testid="job-check-rfid-widget"
                    />
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Paper>
      )}

      {selectedJob && (
        <UpdateProductionJobModal
          open={showUpdateModal}
          onClose={() => {
            setShowUpdateModal(false);
            setSelectedJob(undefined);
          }}
          job={selectedJob}
          cells={cells}
        />
      )}
    </Container>
  );
}

const HeaderStat = ({
  icon,
  label,
  value,
  detail,
  "data-testid": dataTestId,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  "data-testid": string;
}) => (
  <Stack
    data-testid={dataTestId}
    spacing={0.5}
    sx={{
      flex: 1,
      minWidth: 0,
      bgcolor: "rgba(255, 255, 255, 0.14)",
      borderRadius: 1,
      p: 1.5,
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center">
      {icon}
      <Typography variant="caption" sx={{ opacity: 0.8 }}>
        {label}
      </Typography>
    </Stack>
    <Typography fontWeight={800} sx={{ overflowWrap: "anywhere" }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ opacity: 0.75 }}>
      {detail}
    </Typography>
  </Stack>
);

const DetailPanel = ({
  rows,
  "data-testid": dataTestId,
}: {
  rows: Array<[string, string, string?]>;
  "data-testid": string;
}) => (
  <Stack
    data-testid={dataTestId}
    spacing={1}
    sx={{
      height: "100%",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
    }}
  >
    {rows.map(([label, value, color]) => (
      <Stack
        key={label}
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="overline"
          fontWeight={700}
          color={color}
          sx={{ textAlign: "right", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      </Stack>
    ))}
  </Stack>
);
